import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import LoadingScreen from './components/LoadingScreen';
import AuthPage from './components/AuthPage';
import PublicLandingPage from './components/PublicLandingPage';
import SiteScopeApp from './components/SiteScopeApp';
import OfflineBanner from './components/OfflineBanner';
import ToastDisplay from './components/ToastDisplay';
import DADeepLink from './components/DADeepLink';
import TermsPage from './components/TermsPage';
import PrivacyPage from './components/PrivacyPage';
import FirmAuditPage from './components/FirmAuditPage';
import FirmAuditAdminPage from './components/FirmAuditAdminPage';
import ResetPage from './components/ResetPage';
import SourceDocumentPausedPage from './components/SourceDocumentPausedPage';
import { parseCurrentDeepLink } from './lib/deepLinks';
import { useOfflineSync } from './hooks/useOfflineQueue';
import { useIdentityChangePurge } from './hooks/useIdentityChangePurge';

function AppContent() {
  const { loading, session } = useAuth();
  useIdentityChangePurge(session);
  useOfflineSync(session);
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (loading) return <LoadingScreen />;
  if (!session && path === '/') return <PublicLandingPage />;
  if (!session) return <AuthPage />;
  // A different authenticated identity must receive a fresh component tree.
  // Without the key, account B renders once with account A's hook state before
  // passive rehydration effects can clear it.
  return <SubscriptionProvider key={session.user.id}><SiteScopeApp /></SubscriptionProvider>;
}

function AppRouter() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (path === '/admin/audits' || path === '/admin/audit') {
    return <FirmAuditAdminPage />;
  }
  const deepLink = parseCurrentDeepLink();
  if (deepLink?.type === 'da' && !deepLink.daId) {
    return <DADeepLink appNumber={deepLink.appNumber} council={deepLink.council} />;
  }
  if (deepLink) {
    sessionStorage.setItem('ss_pending_deeplink', JSON.stringify(deepLink));
  }
  return <AppContent />;
}

function PublicShell({ children }) {
  return (
    <ThemeProvider>
      <OfflineBanner />
      <ToastDisplay />
      {children}
    </ThemeProvider>
  );
}

export default function App() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (path === '/reset' || path === '/safe-mode') return <ResetPage />;
  if (path === '/source-document' || path === '/source-pdf') return <SourceDocumentPausedPage />;
  if (path === '/terms') return <PublicShell><TermsPage /></PublicShell>;
  if (path === '/privacy') return <PublicShell><PrivacyPage /></PublicShell>;
  if (path === '/reset-password') return <PublicShell><AuthPage initialMode="update" /></PublicShell>;
  if (path.startsWith('/audit/')) return <PublicShell><FirmAuditPage /></PublicShell>;

  return (
    <ThemeProvider>
      <AuthProvider>
        <OfflineBanner />
        <ToastDisplay />
        <AppRouter />
      </AuthProvider>
    </ThemeProvider>
  );
}
